import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import FormControlLabel from '@mui/material/FormControlLabel'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { ClubEvent, EventAttendee } from '@jupi/shared'
import { ApiError, RemoteContent, Title, api, formatRange, useSnackbar } from '@jupi/ui'

export default function EventsPage() {
  const { t } = useTranslation()
  const [showPast, setShowPast] = useState(false)
  const { data: events = [], isLoading, error } = useQuery({
    queryKey: ['events', showPast],
    queryFn: () =>
      api.get<{ events: ClubEvent[] }>(`/api/events${showPast ? '?all=true' : ''}`).then((r) => r.events),
  })

  return (
    <Box>
      <Title label={t('event.title')} />
      <Stack spacing={2}>
        <CalendarLinkCard />
        <FormControlLabel
          control={<Switch checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />}
          label={t('event.showPast')}
        />
        <RemoteContent isLoading={isLoading} error={error}>
          {events.length === 0 && !isLoading ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {t('event.none')}
            </Typography>
          ) : (
            events.map((event) => <EventCard key={event.eventId} event={event} />)
          )}
        </RemoteContent>
      </Stack>
    </Box>
  )
}

function EventCard({ event }: { event: ClubEvent }) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const [showList, setShowList] = useState(false)
  const ended = Date.parse(event.endsAt) <= Date.now()
  const full = event.maxAttendees !== null && event.attendeeCount >= event.maxAttendees

  const { data: attendees = [], isLoading } = useQuery({
    queryKey: ['events', event.eventId, 'attendees'],
    queryFn: () =>
      api.get<{ attendees: EventAttendee[] }>(`/api/events/${event.eventId}/attendees`).then((r) => r.attendees),
    enabled: event.attendanceEnabled && showList,
  })

  const toggle = useMutation({
    mutationFn: () =>
      event.attending
        ? api.delete(`/api/events/${event.eventId}/attendance`)
        : api.post(`/api/events/${event.eventId}/attendance`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['events'] }),
    onError: (err) =>
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' }),
  })

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {event.title}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            color={event.visibility === 'public' ? 'primary' : 'default'}
            label={t(`event.${event.visibility}`)}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {formatRange(event.startsAt, event.endsAt)}
          {event.location ? ` · ${event.location}` : ''}
        </Typography>
        {event.description ? (
          <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
            {event.description}
          </Typography>
        ) : null}

        {event.attendanceEnabled ? (
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
              {(ended || full) && !event.attending ? null : (
                <Button
                  size="small"
                  variant={event.attending ? 'outlined' : 'contained'}
                  color={event.attending ? 'inherit' : 'primary'}
                  onClick={() => toggle.mutate()}
                  disabled={toggle.isPending}
                >
                  {event.attending ? t('event.cancelAttendance') : t('event.attend')}
                </Button>
              )}
              {event.attending ? <Chip size="small" color="success" label={t('event.attending')} /> : null}
              {full && !event.attending ? <Chip size="small" color="warning" label={t('event.full')} /> : null}
              <Button size="small" onClick={() => setShowList((v) => !v)} aria-expanded={showList}>
                {event.maxAttendees !== null
                  ? t('event.attendeeCountOf', { count: event.attendeeCount, max: event.maxAttendees })
                  : t('event.attendeeCount', { count: event.attendeeCount })}
              </Button>
            </Stack>
            <Collapse in={showList} unmountOnExit>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {t('event.whosComing')}
              </Typography>
              {!isLoading && attendees.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('event.noAttendees')}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {attendees.map((a) => (
                    <ListItem key={a.userId} disableGutters>
                      <ListItemText primary={a.displayName} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Collapse>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  )
}

// The member's personal subscription URL. The token in it is the credential
// (calendar apps cannot send the session cookie), hence "do not share".
function CalendarLinkCard() {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: ['events', 'calendar-link'],
    queryFn: () => api.get<{ url: string }>('/api/events/calendar-link'),
  })
  const regenerate = useMutation({
    mutationFn: () => api.post<{ url: string }>('/api/events/calendar-link/regenerate'),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['events', 'calendar-link'], fresh)
      showSnackbar(t('event.regenerated'))
    },
  })

  const url = data?.url ?? ''
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      showSnackbar(t('event.copied'))
    } catch {
      showSnackbar(t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('event.subscribe')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('event.subscribeHint')}
        </Typography>
        <TextField fullWidth size="small" value={url} slotProps={{ input: { readOnly: true } }} />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Button variant="contained" size="small" onClick={copy} disabled={!url}>
            {t('event.copyLink')}
          </Button>
          {/* webcal:// hands the URL to the OS's default calendar app. */}
          <Button size="small" href={url.replace(/^https?:/, 'webcal:')} disabled={!url}>
            {t('event.openInCalendar')}
          </Button>
          <Button size="small" color="warning" onClick={() => regenerate.mutate()} disabled={regenerate.isPending}>
            {t('event.regenerate')}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t('event.regenerateHint')}
        </Typography>
      </CardContent>
    </Card>
  )
}
